"use client";

import { useEffect, useState, useMemo, useCallback } from "react";
import Link from "next/link";
import { createClient } from "@/lib/supabase/client";
import { ThemeToggle } from "@/lib/theme";
import {
  Brain,
  RotateCcw,
  ChevronLeft,
  CheckCircle,
  XCircle,
  Trophy,
  BookOpen,
  Shuffle,
  ArrowRight,
  Clock,
  Flame,
  Zap,
} from "lucide-react";
import { sm2, ratingToQuality } from "@/lib/srs";
import type { SRSRating } from "@/lib/srs";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface VocabWord {
  id: string;
  word: string;
  definition: string;
  example: string | null;
  mastered: boolean;
  source: string | null;
  created_at: string;
  ease_factor: number;
  interval_days: number;
  repetitions: number;
  next_review_at: string;
}

type QuizMode = "flashcard" | "multiple-choice" | "spaced-review";
type FilterMode = "all" | "unmastered" | "mastered";
type ViewMode = "list" | "quiz";

/* ------------------------------------------------------------------ */
/*  Time helpers                                                       */
/* ------------------------------------------------------------------ */

function formatTimeUntil(dateStr: string): string {
  const now = new Date();
  const target = new Date(dateStr);
  const diffMs = target.getTime() - now.getTime();

  if (diffMs <= 0) return "Due now";

  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 60) return `${diffMin}m`;
  if (diffHrs < 24) return `${diffHrs}h`;
  if (diffDays === 1) return "1 day";
  return `${diffDays} days`;
}

function isOverdue(dateStr: string): boolean {
  return new Date(dateStr) <= new Date();
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/* ------------------------------------------------------------------ */
/*  Main Page                                                          */
/* ------------------------------------------------------------------ */

export default function VocabularyQuizPage() {
  const [words, setWords] = useState<VocabWord[]>([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [quizMode, setQuizMode] = useState<QuizMode>("flashcard");

  /* ---- Fetch words ---- */
  useEffect(() => {
    async function load() {
      const supabase = createClient();
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }
      setUserId(user.id);

      const { data } = await supabase
        .from("user_vocabulary")
        .select("id, word, definition, example, mastered, source, created_at, ease_factor, interval_days, repetitions, next_review_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (data) setWords(data);
      setLoading(false);
    }
    load();
  }, []);

  /* ---- Filtered words ---- */
  const filtered = useMemo(() => {
    if (filterMode === "unmastered") return words.filter((w) => !w.mastered);
    if (filterMode === "mastered") return words.filter((w) => w.mastered);
    return words;
  }, [words, filterMode]);

  /* ---- Mark mastered ---- */
  const markMastered = useCallback(
    async (id: string, mastered: boolean) => {
      setWords((prev) =>
        prev.map((w) => (w.id === id ? { ...w, mastered } : w))
      );
      const supabase = createClient();
      await supabase
        .from("user_vocabulary")
        .update({ mastered })
        .eq("id", id);
    },
    []
  );

  /* ---- Not logged in ---- */
  if (!loading && !userId) {
    return (
      <Shell>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <BookOpen size={48} className="mx-auto mb-4 text-blue-500" />
            <h2
              className="text-xl font-bold mb-2"
              style={{ color: "var(--text-primary)" }}
            >
              Sign in to access your vocabulary
            </h2>
            <p className="mb-6" style={{ color: "var(--text-muted)" }}>
              Words saved from your TOEFL exams will appear here for review and
              quizzing.
            </p>
            <Link
              href="/auth/callback"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
            >
              Sign In
            </Link>
          </div>
        </div>
      </Shell>
    );
  }

  /* ---- Loading ---- */
  if (loading) {
    return (
      <Shell>
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span style={{ color: "var(--text-muted)" }}>
              Loading vocabulary...
            </span>
          </div>
        </div>
      </Shell>
    );
  }

  /* ---- No words ---- */
  if (words.length === 0) {
    return (
      <Shell>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <Brain size={48} className="mx-auto mb-4 text-blue-500" />
            <h2
              className="text-xl font-bold mb-2"
              style={{ color: "var(--text-primary)" }}
            >
              No saved words yet
            </h2>
            <p className="mb-6" style={{ color: "var(--text-muted)" }}>
              Take a TOEFL exam and your vocabulary words will be saved here
              automatically during scoring.
            </p>
            <Link
              href="/toefl"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
            >
              Start a Practice Exam
            </Link>
          </div>
        </div>
      </Shell>
    );
  }

  /* ---- Main content ---- */
  const masteredCount = words.filter((w) => w.mastered).length;

  return (
    <Shell>
      <div className="max-w-3xl mx-auto px-4 py-6 flex-1">
        {/* Header stats */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1
              className="text-2xl font-bold"
              style={{ color: "var(--text-primary)" }}
            >
              My Vocabulary
            </h1>
            <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
              {words.length} words saved &middot; {masteredCount} mastered
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* View mode toggle */}
            <button
              onClick={() => setViewMode("list")}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                viewMode === "list"
                  ? "bg-blue-600 text-white"
                  : "hover:opacity-80"
              }`}
              style={
                viewMode !== "list"
                  ? {
                      backgroundColor: "var(--bg-secondary)",
                      color: "var(--text-secondary)",
                    }
                  : undefined
              }
            >
              Word List
            </button>
            <button
              onClick={() => setViewMode("quiz")}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                viewMode === "quiz"
                  ? "bg-blue-600 text-white"
                  : "hover:opacity-80"
              }`}
              style={
                viewMode !== "quiz"
                  ? {
                      backgroundColor: "var(--bg-secondary)",
                      color: "var(--text-secondary)",
                    }
                  : undefined
              }
            >
              Quiz Mode
            </button>
          </div>
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          {(["all", "unmastered", "mastered"] as FilterMode[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilterMode(f)}
              className={`px-3 py-1 text-xs rounded-full font-medium capitalize transition-colors ${
                filterMode === f
                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                  : ""
              }`}
              style={
                filterMode !== f
                  ? {
                      backgroundColor: "var(--bg-secondary)",
                      color: "var(--text-muted)",
                    }
                  : undefined
              }
            >
              {f === "all"
                ? `All (${words.length})`
                : f === "unmastered"
                  ? `Still Learning (${words.filter((w) => !w.mastered).length})`
                  : `Mastered (${masteredCount})`}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div
            className="text-center py-16"
            style={{ color: "var(--text-muted)" }}
          >
            No words match this filter.
          </div>
        ) : viewMode === "list" ? (
          <WordList words={filtered} onToggleMastered={markMastered} />
        ) : (
          <QuizModeSection
            words={filtered}
            allWords={words}
            quizMode={quizMode}
            setQuizMode={setQuizMode}
            onMarkMastered={markMastered}
            onUpdateWords={setWords}
          />
        )}
      </div>
    </Shell>
  );
}

/* ================================================================== */
/*  Shell (nav wrapper)                                                */
/* ================================================================== */

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: "var(--bg-primary)" }}
    >
      <nav
        className="border-b"
        style={{
          borderColor: "var(--border)",
          backgroundColor: "var(--bg-card)",
        }}
      >
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              href="/toefl/vocabulary"
              className="flex items-center gap-1.5 text-sm"
              style={{ color: "var(--text-muted)" }}
            >
              <ChevronLeft size={16} />
              Vocabulary
            </Link>
            <span style={{ color: "var(--border)" }}>|</span>
            <div className="flex items-center gap-2">
              <Brain size={18} className="text-blue-500" />
              <span
                className="font-semibold text-sm"
                style={{ color: "var(--text-primary)" }}
              >
                Vocabulary Quiz
              </span>
            </div>
          </div>
          <ThemeToggle />
        </div>
      </nav>
      {children}
    </div>
  );
}

/* ================================================================== */
/*  Word List View                                                     */
/* ================================================================== */

function WordList({
  words,
  onToggleMastered,
}: {
  words: VocabWord[];
  onToggleMastered: (id: string, mastered: boolean) => void;
}) {
  return (
    <div className="space-y-3">
      {words.map((w) => {
        const due = isOverdue(w.next_review_at);
        return (
          <div
            key={w.id}
            className={`rounded-xl border p-4 transition-colors ${
              due
                ? "border-amber-400 dark:border-amber-600 ring-1 ring-amber-200 dark:ring-amber-800"
                : ""
            }`}
            style={{
              backgroundColor: "var(--bg-card)",
              borderColor: due ? undefined : "var(--border)",
            }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span
                    className="font-semibold"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {w.word}
                  </span>
                  {w.mastered && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                      Mastered
                    </span>
                  )}
                  {due && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 flex items-center gap-1">
                      <Clock size={10} />
                      Due for review
                    </span>
                  )}
                </div>
                <p
                  className="text-sm mb-1"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {w.definition}
                </p>
                {w.example && (
                  <p
                    className="text-xs italic"
                    style={{ color: "var(--text-muted)" }}
                  >
                    &ldquo;{w.example}&rdquo;
                  </p>
                )}
                <div className="flex items-center gap-3 mt-1.5">
                  {w.source && (
                    <p
                      className="text-xs"
                      style={{ color: "var(--text-muted)" }}
                    >
                      Source: {w.source}
                    </p>
                  )}
                  <p
                    className="text-xs flex items-center gap-1"
                    style={{ color: "var(--text-muted)" }}
                  >
                    <Clock size={10} />
                    {due
                      ? "Review now"
                      : `Next review: ${formatTimeUntil(w.next_review_at)}`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => onToggleMastered(w.id, !w.mastered)}
                className={`shrink-0 px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                  w.mastered
                    ? "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
                    : "bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900 dark:text-green-300 dark:hover:bg-green-800"
                }`}
              >
                {w.mastered ? "Unmaster" : "Mark Mastered"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ================================================================== */
/*  Quiz Mode Section                                                  */
/* ================================================================== */

function QuizModeSection({
  words,
  allWords,
  quizMode,
  setQuizMode,
  onMarkMastered,
  onUpdateWords,
}: {
  words: VocabWord[];
  allWords: VocabWord[];
  quizMode: QuizMode;
  setQuizMode: (m: QuizMode) => void;
  onMarkMastered: (id: string, mastered: boolean) => void;
  onUpdateWords: React.Dispatch<React.SetStateAction<VocabWord[]>>;
}) {
  const canMultipleChoice = allWords.length >= 4;

  // Count words due for review
  const dueCount = useMemo(
    () => allWords.filter((w) => isOverdue(w.next_review_at)).length,
    [allWords]
  );

  return (
    <div>
      {/* Quiz mode selector */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <button
          onClick={() => setQuizMode("flashcard")}
          className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors flex items-center gap-1.5 ${
            quizMode === "flashcard"
              ? "bg-blue-600 text-white"
              : "hover:opacity-80"
          }`}
          style={
            quizMode !== "flashcard"
              ? {
                  backgroundColor: "var(--bg-secondary)",
                  color: "var(--text-secondary)",
                }
              : undefined
          }
        >
          <Shuffle size={14} />
          Flashcards
        </button>
        <button
          onClick={() => canMultipleChoice && setQuizMode("multiple-choice")}
          disabled={!canMultipleChoice}
          className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors flex items-center gap-1.5 ${
            quizMode === "multiple-choice"
              ? "bg-blue-600 text-white"
              : !canMultipleChoice
                ? "opacity-40 cursor-not-allowed"
                : "hover:opacity-80"
          }`}
          style={
            quizMode !== "multiple-choice"
              ? {
                  backgroundColor: "var(--bg-secondary)",
                  color: "var(--text-secondary)",
                }
              : undefined
          }
          title={
            !canMultipleChoice
              ? "Need at least 4 words for multiple choice"
              : undefined
          }
        >
          <CheckCircle size={14} />
          Multiple Choice
        </button>
        <button
          onClick={() => setQuizMode("spaced-review")}
          className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors flex items-center gap-1.5 ${
            quizMode === "spaced-review"
              ? "bg-blue-600 text-white"
              : "hover:opacity-80"
          }`}
          style={
            quizMode !== "spaced-review"
              ? {
                  backgroundColor: "var(--bg-secondary)",
                  color: "var(--text-secondary)",
                }
              : undefined
          }
        >
          <Clock size={14} />
          Spaced Review
          {dueCount > 0 && (
            <span
              className={`ml-1 px-1.5 py-0.5 text-xs rounded-full font-bold ${
                quizMode === "spaced-review"
                  ? "bg-white/20 text-white"
                  : "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300"
              }`}
            >
              {dueCount}
            </span>
          )}
        </button>
        {!canMultipleChoice && quizMode !== "spaced-review" && (
          <span
            className="text-xs"
            style={{ color: "var(--text-muted)" }}
          >
            (Need 4+ words for multiple choice)
          </span>
        )}
      </div>

      {quizMode === "flashcard" ? (
        <FlashcardQuiz words={words} onMarkMastered={onMarkMastered} />
      ) : quizMode === "multiple-choice" ? (
        <MultipleChoiceQuiz
          words={words}
          allWords={allWords}
          onMarkMastered={onMarkMastered}
        />
      ) : (
        <SpacedReviewQuiz allWords={allWords} onUpdateWords={onUpdateWords} />
      )}
    </div>
  );
}

/* ================================================================== */
/*  Flashcard Quiz                                                     */
/* ================================================================== */

function FlashcardQuiz({
  words,
  onMarkMastered,
}: {
  words: VocabWord[];
  onMarkMastered: (id: string, mastered: boolean) => void;
}) {
  const shuffled = useMemo(() => shuffle(words), [words]);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [results, setResults] = useState<
    { word: VocabWord; gotIt: boolean }[]
  >([]);
  const [done, setDone] = useState(false);

  const card = shuffled[index];
  const total = shuffled.length;

  function handleGotIt() {
    onMarkMastered(card.id, true);
    setResults((r) => [...r, { word: card, gotIt: true }]);
    advance();
  }

  function handleStillLearning() {
    setResults((r) => [...r, { word: card, gotIt: false }]);
    advance();
  }

  function advance() {
    if (index + 1 >= total) {
      setDone(true);
    } else {
      setIndex((i) => i + 1);
      setFlipped(false);
    }
  }

  function restart() {
    setIndex(0);
    setFlipped(false);
    setResults([]);
    setDone(false);
  }

  if (done) {
    const gotItCount = results.filter((r) => r.gotIt).length;
    const stillLearning = results.filter((r) => !r.gotIt);
    return (
      <FlashcardSummary
        total={total}
        gotItCount={gotItCount}
        stillLearning={stillLearning.map((r) => r.word)}
        onRestart={restart}
      />
    );
  }

  return (
    <div>
      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs mb-1">
          <span style={{ color: "var(--text-muted)" }}>
            {index + 1} of {total}
          </span>
          <span style={{ color: "var(--text-muted)" }}>
            {Math.round(((index) / total) * 100)}% complete
          </span>
        </div>
        <div
          className="h-2 rounded-full overflow-hidden"
          style={{ backgroundColor: "var(--bg-secondary)" }}
        >
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-300"
            style={{ width: `${(index / total) * 100}%` }}
          />
        </div>
      </div>

      {/* Flashcard */}
      <div
        className="perspective-1000 cursor-pointer mb-4"
        onClick={() => setFlipped((f) => !f)}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") setFlipped((f) => !f);
        }}
        role="button"
        tabIndex={0}
        aria-label={flipped ? "Showing definition" : "Tap to reveal definition"}
      >
        <div
          className="relative w-full transition-transform duration-500"
          style={{
            transformStyle: "preserve-3d",
            transform: flipped ? "rotateY(180deg)" : "rotateY(0)",
            minHeight: "240px",
          }}
        >
          {/* Front */}
          <div
            className="absolute inset-0 rounded-2xl border p-8 flex flex-col items-center justify-center backface-hidden"
            style={{
              backgroundColor: "var(--bg-card)",
              borderColor: "var(--border)",
              backfaceVisibility: "hidden",
            }}
          >
            <span
              className="text-3xl font-bold mb-3"
              style={{ color: "var(--text-primary)" }}
            >
              {card.word}
            </span>
            <span className="text-sm" style={{ color: "var(--text-muted)" }}>
              Tap to reveal definition
            </span>
          </div>

          {/* Back */}
          <div
            className="absolute inset-0 rounded-2xl border p-8 flex flex-col items-center justify-center backface-hidden"
            style={{
              backgroundColor: "var(--bg-card)",
              borderColor: "var(--border)",
              backfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
            }}
          >
            <p
              className="text-lg font-medium text-center mb-3"
              style={{ color: "var(--text-primary)" }}
            >
              {card.definition}
            </p>
            {card.example && (
              <p
                className="text-sm italic text-center"
                style={{ color: "var(--text-muted)" }}
              >
                &ldquo;{card.example}&rdquo;
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Action buttons (only visible when flipped) */}
      {flipped && (
        <div className="flex gap-3">
          <button
            onClick={handleStillLearning}
            className="flex-1 py-3 rounded-xl font-medium text-sm transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 flex items-center justify-center gap-2"
          >
            <RotateCcw size={16} />
            Still learning
          </button>
          <button
            onClick={handleGotIt}
            className="flex-1 py-3 rounded-xl font-medium text-sm transition-colors bg-green-600 text-white hover:bg-green-700 flex items-center justify-center gap-2"
          >
            <CheckCircle size={16} />
            Got it
          </button>
        </div>
      )}
    </div>
  );
}

/* ---- Flashcard Summary ---- */

function FlashcardSummary({
  total,
  gotItCount,
  stillLearning,
  onRestart,
}: {
  total: number;
  gotItCount: number;
  stillLearning: VocabWord[];
  onRestart: () => void;
}) {
  return (
    <div className="text-center">
      <Trophy size={48} className="mx-auto mb-4 text-yellow-500" />
      <h2
        className="text-2xl font-bold mb-2"
        style={{ color: "var(--text-primary)" }}
      >
        Session Complete!
      </h2>
      <p className="mb-6" style={{ color: "var(--text-muted)" }}>
        You reviewed {total} words
      </p>

      <div className="grid grid-cols-2 gap-3 max-w-xs mx-auto mb-6">
        <div className="rounded-xl p-4 bg-green-50 dark:bg-green-900/30 text-center">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {gotItCount}
          </div>
          <div className="text-xs text-green-600 dark:text-green-400 mt-0.5">
            Mastered
          </div>
        </div>
        <div className="rounded-xl p-4 bg-orange-50 dark:bg-orange-900/30 text-center">
          <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
            {stillLearning.length}
          </div>
          <div className="text-xs text-orange-600 dark:text-orange-400 mt-0.5">
            Still Learning
          </div>
        </div>
      </div>

      {stillLearning.length > 0 && (
        <div className="mb-6 text-left max-w-sm mx-auto">
          <h3
            className="text-sm font-semibold mb-2"
            style={{ color: "var(--text-secondary)" }}
          >
            Words to review:
          </h3>
          <div className="space-y-1">
            {stillLearning.map((w) => (
              <div
                key={w.id}
                className="text-sm px-3 py-1.5 rounded-lg"
                style={{
                  backgroundColor: "var(--bg-secondary)",
                  color: "var(--text-secondary)",
                }}
              >
                <span className="font-medium">{w.word}</span>
                <span style={{ color: "var(--text-muted)" }}>
                  {" "}
                  &mdash; {w.definition}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={onRestart}
        className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
      >
        <RotateCcw size={16} />
        Quiz Again
      </button>
    </div>
  );
}

/* ================================================================== */
/*  Multiple Choice Quiz                                               */
/* ================================================================== */

function MultipleChoiceQuiz({
  words,
  allWords,
  onMarkMastered,
}: {
  words: VocabWord[];
  allWords: VocabWord[];
  onMarkMastered: (id: string, mastered: boolean) => void;
}) {
  const questions = useMemo(() => {
    const shuffledWords = shuffle(words);
    return shuffledWords.map((w) => {
      const distractors = shuffle(
        allWords.filter((x) => x.id !== w.id)
      )
        .slice(0, 3)
        .map((x) => x.definition);
      const options = shuffle([w.definition, ...distractors]);
      return {
        word: w,
        options,
        correctIndex: options.indexOf(w.definition),
      };
    });
  }, [words, allWords]);

  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [results, setResults] = useState<
    { word: VocabWord; correct: boolean }[]
  >([]);
  const [done, setDone] = useState(false);

  const q = questions[index];
  const answered = selected !== null;
  const isCorrect = selected === q?.correctIndex;

  function handleSelect(idx: number) {
    if (answered) return;
    setSelected(idx);
    const correct = idx === q.correctIndex;
    setResults((r) => [...r, { word: q.word, correct }]);
    if (correct) {
      onMarkMastered(q.word.id, true);
    }
  }

  function next() {
    if (index + 1 >= questions.length) {
      setDone(true);
    } else {
      setIndex((i) => i + 1);
      setSelected(null);
    }
  }

  function restart() {
    setIndex(0);
    setSelected(null);
    setResults([]);
    setDone(false);
  }

  if (done) {
    const correctCount = results.filter((r) => r.correct).length;
    const incorrect = results.filter((r) => !r.correct);
    return (
      <div className="text-center">
        <Trophy size={48} className="mx-auto mb-4 text-yellow-500" />
        <h2
          className="text-2xl font-bold mb-2"
          style={{ color: "var(--text-primary)" }}
        >
          Quiz Complete!
        </h2>
        <p className="mb-2" style={{ color: "var(--text-muted)" }}>
          Score: {correctCount} / {results.length}
        </p>
        <p className="text-3xl font-bold mb-6" style={{ color: "var(--text-primary)" }}>
          {Math.round((correctCount / results.length) * 100)}%
        </p>

        <div className="grid grid-cols-2 gap-3 max-w-xs mx-auto mb-6">
          <div className="rounded-xl p-4 bg-green-50 dark:bg-green-900/30 text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {correctCount}
            </div>
            <div className="text-xs text-green-600 dark:text-green-400 mt-0.5">
              Correct
            </div>
          </div>
          <div className="rounded-xl p-4 bg-red-50 dark:bg-red-900/30 text-center">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {incorrect.length}
            </div>
            <div className="text-xs text-red-600 dark:text-red-400 mt-0.5">
              Incorrect
            </div>
          </div>
        </div>

        {incorrect.length > 0 && (
          <div className="mb-6 text-left max-w-sm mx-auto">
            <h3
              className="text-sm font-semibold mb-2"
              style={{ color: "var(--text-secondary)" }}
            >
              Missed words:
            </h3>
            <div className="space-y-1">
              {incorrect.map((r) => (
                <div
                  key={r.word.id}
                  className="text-sm px-3 py-1.5 rounded-lg"
                  style={{
                    backgroundColor: "var(--bg-secondary)",
                    color: "var(--text-secondary)",
                  }}
                >
                  <span className="font-medium">{r.word.word}</span>
                  <span style={{ color: "var(--text-muted)" }}>
                    {" "}
                    &mdash; {r.word.definition}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={restart}
          className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
        >
          <RotateCcw size={16} />
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs mb-1">
          <span style={{ color: "var(--text-muted)" }}>
            Question {index + 1} of {questions.length}
          </span>
          <span style={{ color: "var(--text-muted)" }}>
            {results.filter((r) => r.correct).length} correct
          </span>
        </div>
        <div
          className="h-2 rounded-full overflow-hidden"
          style={{ backgroundColor: "var(--bg-secondary)" }}
        >
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-300"
            style={{ width: `${(index / questions.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Question card */}
      <div
        className="rounded-2xl border p-6 mb-4"
        style={{
          backgroundColor: "var(--bg-card)",
          borderColor: "var(--border)",
        }}
      >
        <p className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>
          What is the definition of:
        </p>
        <h3
          className="text-2xl font-bold mb-5"
          style={{ color: "var(--text-primary)" }}
        >
          {q.word.word}
        </h3>

        <div className="space-y-2">
          {q.options.map((opt, i) => {
            let style = "";
            if (answered) {
              if (i === q.correctIndex)
                style =
                  "border-green-500 bg-green-50 dark:bg-green-900/30 dark:border-green-600";
              else if (i === selected && !isCorrect)
                style =
                  "border-red-500 bg-red-50 dark:bg-red-900/30 dark:border-red-600";
            }
            return (
              <button
                key={i}
                onClick={() => handleSelect(i)}
                disabled={answered}
                className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all ${
                  answered ? "" : "hover:border-blue-400 dark:hover:border-blue-500"
                } ${style}`}
                style={
                  !style
                    ? {
                        backgroundColor: "var(--bg-card)",
                        borderColor: "var(--border)",
                        color: "var(--text-secondary)",
                      }
                    : { color: "var(--text-secondary)" }
                }
              >
                <div className="flex items-start gap-3">
                  <span
                    className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{
                      backgroundColor: "var(--bg-secondary)",
                      color: "var(--text-muted)",
                    }}
                  >
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span>{opt}</span>
                  {answered && i === q.correctIndex && (
                    <CheckCircle
                      size={18}
                      className="shrink-0 ml-auto text-green-600 dark:text-green-400"
                    />
                  )}
                  {answered && i === selected && !isCorrect && (
                    <XCircle
                      size={18}
                      className="shrink-0 ml-auto text-red-600 dark:text-red-400"
                    />
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Show correct answer feedback */}
        {answered && !isCorrect && (
          <div className="mt-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-sm">
            <span className="font-medium text-blue-700 dark:text-blue-300">
              Correct answer:
            </span>{" "}
            <span style={{ color: "var(--text-secondary)" }}>
              {q.word.definition}
            </span>
            {q.word.example && (
              <p className="mt-1 italic text-xs" style={{ color: "var(--text-muted)" }}>
                &ldquo;{q.word.example}&rdquo;
              </p>
            )}
          </div>
        )}
      </div>

      {/* Next button */}
      {answered && (
        <button
          onClick={next}
          className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
        >
          {index + 1 >= questions.length ? "See Results" : "Next Question"}
          <ArrowRight size={16} />
        </button>
      )}
    </div>
  );
}

/* ================================================================== */
/*  Spaced Review Quiz (SM-2)                                          */
/* ================================================================== */

function SpacedReviewQuiz({
  allWords,
  onUpdateWords,
}: {
  allWords: VocabWord[];
  onUpdateWords: React.Dispatch<React.SetStateAction<VocabWord[]>>;
}) {
  // Words due for review, sorted most overdue first
  const dueWords = useMemo(
    () =>
      allWords
        .filter((w) => isOverdue(w.next_review_at))
        .sort(
          (a, b) =>
            new Date(a.next_review_at).getTime() -
            new Date(b.next_review_at).getTime()
        ),
    [allWords]
  );

  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [reviewedCount, setReviewedCount] = useState(0);
  const [sessionDone, setSessionDone] = useState(false);
  const [saving, setSaving] = useState(false);

  // Find the next upcoming review if none are due
  const nextReviewWord = useMemo(() => {
    if (dueWords.length > 0) return null;
    const future = allWords
      .filter((w) => !isOverdue(w.next_review_at))
      .sort(
        (a, b) =>
          new Date(a.next_review_at).getTime() -
          new Date(b.next_review_at).getTime()
      );
    return future[0] || null;
  }, [allWords, dueWords]);

  const card = dueWords[index];
  const remaining = dueWords.length - index;

  async function handleRate(rating: SRSRating) {
    if (!card || saving) return;
    setSaving(true);

    const quality = ratingToQuality(rating);
    const result = sm2({
      quality,
      repetitions: card.repetitions,
      ease_factor: card.ease_factor,
      interval_days: card.interval_days,
    });

    // Update in Supabase
    const supabase = createClient();
    await supabase
      .from("user_vocabulary")
      .update({
        ease_factor: result.ease_factor,
        interval_days: result.interval_days,
        repetitions: result.repetitions,
        next_review_at: result.next_review_at,
      })
      .eq("id", card.id);

    // Update local state
    onUpdateWords((prev) =>
      prev.map((w) =>
        w.id === card.id
          ? {
              ...w,
              ease_factor: result.ease_factor,
              interval_days: result.interval_days,
              repetitions: result.repetitions,
              next_review_at: result.next_review_at,
            }
          : w
      )
    );

    setReviewedCount((c) => c + 1);
    setSaving(false);

    if (index + 1 >= dueWords.length) {
      setSessionDone(true);
    } else {
      setIndex((i) => i + 1);
      setFlipped(false);
    }
  }

  // All caught up state
  if (dueWords.length === 0 || sessionDone) {
    return (
      <div className="text-center py-12">
        <CheckCircle size={48} className="mx-auto mb-4 text-green-500" />
        <h2
          className="text-2xl font-bold mb-2"
          style={{ color: "var(--text-primary)" }}
        >
          {sessionDone ? "Session Complete!" : "All caught up!"}
        </h2>
        {sessionDone && (
          <p className="mb-2" style={{ color: "var(--text-muted)" }}>
            You reviewed {reviewedCount} word{reviewedCount !== 1 ? "s" : ""}{" "}
            this session.
          </p>
        )}
        {nextReviewWord ? (
          <p style={{ color: "var(--text-muted)" }}>
            Next review in{" "}
            <span className="font-semibold" style={{ color: "var(--text-secondary)" }}>
              {formatTimeUntil(nextReviewWord.next_review_at)}
            </span>
          </p>
        ) : (
          <p style={{ color: "var(--text-muted)" }}>
            No words scheduled for review yet.
          </p>
        )}
        {sessionDone && (
          <button
            onClick={() => {
              setIndex(0);
              setFlipped(false);
              setReviewedCount(0);
              setSessionDone(false);
            }}
            className="mt-6 px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
          >
            <RotateCcw size={16} />
            Done
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs mb-1">
          <span style={{ color: "var(--text-muted)" }}>
            {reviewedCount} reviewed, {remaining} remaining today
          </span>
          <span className="flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
            <Flame size={12} className="text-orange-500" />
            {reviewedCount} this session
          </span>
        </div>
        <div
          className="h-2 rounded-full overflow-hidden"
          style={{ backgroundColor: "var(--bg-secondary)" }}
        >
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-300"
            style={{
              width: `${(reviewedCount / (reviewedCount + remaining)) * 100}%`,
            }}
          />
        </div>
      </div>

      {/* Flashcard */}
      <div
        className="perspective-1000 cursor-pointer mb-4"
        onClick={() => setFlipped((f) => !f)}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") setFlipped((f) => !f);
        }}
        role="button"
        tabIndex={0}
        aria-label={flipped ? "Showing definition" : "Tap to reveal definition"}
      >
        <div
          className="relative w-full transition-transform duration-500"
          style={{
            transformStyle: "preserve-3d",
            transform: flipped ? "rotateY(180deg)" : "rotateY(0)",
            minHeight: "240px",
          }}
        >
          {/* Front */}
          <div
            className="absolute inset-0 rounded-2xl border p-8 flex flex-col items-center justify-center backface-hidden"
            style={{
              backgroundColor: "var(--bg-card)",
              borderColor: "var(--border)",
              backfaceVisibility: "hidden",
            }}
          >
            <span
              className="text-3xl font-bold mb-3"
              style={{ color: "var(--text-primary)" }}
            >
              {card.word}
            </span>
            <span className="text-sm" style={{ color: "var(--text-muted)" }}>
              Tap to reveal definition
            </span>
            {card.interval_days > 0 && (
              <span
                className="text-xs mt-3 flex items-center gap-1"
                style={{ color: "var(--text-muted)" }}
              >
                <Zap size={10} />
                Interval: {card.interval_days} day{card.interval_days !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {/* Back */}
          <div
            className="absolute inset-0 rounded-2xl border p-8 flex flex-col items-center justify-center backface-hidden"
            style={{
              backgroundColor: "var(--bg-card)",
              borderColor: "var(--border)",
              backfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
            }}
          >
            <p
              className="text-lg font-medium text-center mb-3"
              style={{ color: "var(--text-primary)" }}
            >
              {card.definition}
            </p>
            {card.example && (
              <p
                className="text-sm italic text-center"
                style={{ color: "var(--text-muted)" }}
              >
                &ldquo;{card.example}&rdquo;
              </p>
            )}
          </div>
        </div>
      </div>

      {/* SRS Rating buttons (only visible when flipped) */}
      {flipped && (
        <div className="flex gap-3">
          <button
            onClick={() => handleRate("again")}
            disabled={saving}
            className="flex-1 py-3 rounded-xl font-medium text-sm transition-colors bg-red-600 text-white hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <RotateCcw size={16} />
            Again
            <span className="text-xs opacity-75">(&lt;1d)</span>
          </button>
          <button
            onClick={() => handleRate("good")}
            disabled={saving}
            className="flex-1 py-3 rounded-xl font-medium text-sm transition-colors bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <CheckCircle size={16} />
            Good
          </button>
          <button
            onClick={() => handleRate("easy")}
            disabled={saving}
            className="flex-1 py-3 rounded-xl font-medium text-sm transition-colors bg-green-600 text-white hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Zap size={16} />
            Easy
          </button>
        </div>
      )}
    </div>
  );
}
